import { lazy, Suspense } from "react"
import { createBrowserRouter, Navigate, Outlet, useParams } from "react-router-dom"
import { DashboardProvider } from "@/context-state/DashboardContext.jsx"
import { DashboardDataProvider } from "@/pages/dashboard/DashboardDataProvider.jsx"
import { AUTH_BASE_PATH, DASHBOARD_ROOT } from '@/consts/index.js'
import { MainLoader } from '@/components/loader/MainLoader.jsx'
import { SnapshotDataProvider } from '@/pages/public/snapshot/SnapshotDataProvider.jsx'
import SharedReportDataProvider from '../pages/public/shared-report/SharedReportDataProvider.jsx'
import AuthGuard from "@/guards/AuthGuard.jsx"
import DashboardLayout from "@/layouts/dashboard"
import AccountGuard from '@/guards/AccountGuard.jsx'
import SpaceDefault from '@/pages/space/SpaceDefault.jsx'
import AccountDefault from '@/pages/account/AccountDefault.jsx'
import AuthLayout from '@/layouts/auth/index.jsx'
// Eagerly imported (not lazy): onboarding is a full-screen takeover reached
// right after register, and a lazy Suspense loader flashed on the way in.
import OnboardingPage from '@/pages/dashboard/onboarding/OnboardingPage.jsx'
import { CreativeAnalyticsProvider } from '@/context-state/CreativeAnalyticsContext.jsx'
import { ResearchProvider } from '@/context-state/ResearchContext.jsx'
import { ResearchDataProvider } from '@/pages/dashboard/research/ResearchDataProvider.jsx'
import { ExternalAdDataProvider } from '@/pages/public/external-ad/ExternalAdDataProvider.jsx'
import { PaymentProvider } from '@/context-state/PaymentContext.jsx'
import { FreeTierLockGuard } from '@/guards/FreeTierLockGuard.jsx'
import { PaymentDataProvider } from '@/pages/space/PaymentDataProvider.jsx'
import { ErrorBoundary } from 'react-error-boundary'
import { AppErrorBoundary, handleDynamicRouteError, RouteErrorBoundary } from '@/components/app-error-boundry/AppErrorBoundary.jsx'
import SpaceGuard from '@/guards/SpaceGuard.jsx'
import { AccountSpacesDataProvider } from "@/pages/dashboard/settings/account/account-spaces/AccountSpacesDataProvider.jsx"
import { SpaceMembersDataProvider } from "@/pages/dashboard/settings/space/space-members/SpaceMembersDataProvider.jsx"
import { AccountMembersDataProvider } from "@/pages/dashboard/settings/account/account-members/AccountMembersDataProvider.jsx"
import { LibraryProvider } from "@/context-state/LibraryContext.jsx"
import { BrandDataProvider } from '@/pages/dashboard/research/brand/BrandDataProvider.jsx'
import { BoardDataProvider } from "@/pages/dashboard/research/board/BoardDataProvider.jsx"
import { BrandStrategySnapshopDataProvider } from "@/pages/public/brand-strategy-snapshot/BrandStrategySnapshopDataProvider.jsx"
import EmbeddingsSearchPage from "@/pages/dashboard/EmbeddingsSearchPage.jsx"
import { CreativeAiChatProvider } from '@/context-state/CreativeAiChatContext.jsx'
import { CreativeIntelligenceProvider } from '@/context-state/CreativeIntelligenceContext.jsx'
import { IntelligenceProvider } from '@/context-state/IntelligenceContext.jsx'
import { PublicBoardDataProvider } from "@/pages/public/board/PublicBoardDataProvider.jsx"
import { CreativeShareDataProvider } from "@/pages/public/creative-share/CreativeShareDataProvider.jsx"
import { ChatDataProvider } from "@/pages/dashboard/chat/ChatDataProvider.jsx"


const Loadable = (Component) => (props) => {
    return (
        <ErrorBoundary FallbackComponent={AppErrorBoundary} onError={handleDynamicRouteError}>
            <Suspense fallback={<MainLoader />}>
                <Component {...props} />
            </Suspense>
        </ErrorBoundary>
    )
}


// Legacy /research/* deep-links redirect to /intelligence. Both pages
// stay scoped to the user's current account+space — we read those via
// useParams and build an absolute path so the redirect is unambiguous
// regardless of the matched route's depth.
const ResearchBoardRedirect = () => {
    const { accountId, spaceId, boardId } = useParams()
    return (
        <Navigate
            to={`/account/${accountId}/space/${spaceId}/dashboard/intelligence/boards/${boardId}`}
            replace
        />
    )
}

// Parameterized static redirect — used to map specific legacy paths
// (e.g. /research/all-brands → /intelligence/watching) and the catch-all
// fallback (/research/* → /intelligence/library) without duplicating
// the account/space scoping logic.
const ResearchRedirectTo = ({ dest }) => {
    const { accountId, spaceId } = useParams()
    return (
        <Navigate
            to={`/account/${accountId}/space/${spaceId}/dashboard/intelligence/${dest}`}
            replace
        />
    )
}

// Auth
const SignInPage = Loadable(lazy(() => import('../pages/auth/SignInPage.jsx')))
const RegisterPage = Loadable(lazy(() => import('../pages/auth/RegisterPage.jsx')))
const VerifyAuthPage = Loadable(lazy(() => import('../pages/auth/VerifyAuthPage.jsx')))
const AcceptInvitationPage = Loadable(lazy(() => import('../pages/auth/AcceptInvitationPage.jsx')))
const McpConnectPage = Loadable(lazy(() => import('../pages/auth/McpConnectPage.jsx')))
// Dashboard
const CreativePage = Loadable(lazy(() => import('../pages/dashboard/creative/CreativePage.jsx')))
const LibraryPage = Loadable(lazy(() => import('../pages/dashboard/library/LibraryPage.jsx')))
const FolderPage = Loadable(lazy(() => import('../pages/dashboard/library/FolderPage.jsx')))
const AllBrandsPage = Loadable(lazy(() => import('../pages/dashboard/research/brand/AllBrandsPage.jsx')))
const BrandPage = Loadable(lazy(() => import('../pages/dashboard/research/brand/BrandsPage.jsx')))
const IntelligenceLibraryPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/LibraryPage.jsx')))
const IntelligenceLandingPagesPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/LandingPagesPage.jsx')))
const IntelligenceLandingPageDetailPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/LandingPageDetailPage.jsx')))
const IntelligenceBrandLandingPagesPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/BrandLandingPagesPage.jsx')))
const IntelligenceLibraryHomePage = Loadable(lazy(() => import('../pages/dashboard/intelligence/LibraryHomePage.jsx')))
const IntelligenceLibraryAnswerPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/LibraryAnswerPage.jsx')))
const IntelligenceLibrarySurfacePage = Loadable(lazy(() => import('../pages/dashboard/intelligence/LibrarySurfacePage.jsx')))
const IntelligenceLibraryCommandPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/LibraryCommandPage.jsx')))
const IntelligenceDiscoveryPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/discovery/DiscoveryPage.jsx')))
const IntelligenceAdvertisersPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/AdvertisersPage.jsx')))
const ApiOverviewPage = Loadable(lazy(() => import('../pages/dashboard/api/ApiOverviewPage.jsx')))
const ApiUsagePage = Loadable(lazy(() => import('../pages/dashboard/api/ApiUsagePage.jsx')))
const ApiBillingPage = Loadable(lazy(() => import('../pages/dashboard/api/ApiBillingPage.jsx')))
const ApiSupportPage = Loadable(lazy(() => import('../pages/dashboard/api/ApiSupportPage.jsx')))
const IntelligenceBrandLayout = Loadable(lazy(() => import('../pages/dashboard/intelligence/BrandLayout.jsx')))
const IntelligenceBrandPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/BrandPage.jsx')))
const IntelligenceBrandAnalysisPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/BrandAnalysisPage.jsx')))
const IntelligenceSimilarAdvertisersPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/SimilarAdvertisersPage.jsx')))
const IntelligenceCreatorsPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/CreatorsPage.jsx')))
const IntelligenceRedditPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/RedditPage.jsx')))
const IntelligenceAllAdsPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/AllAdsPage.jsx')))
const IntelligenceAdDetailPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/AdDetailPage.jsx')))
const IntelligenceHookAnalysisPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/HookAnalysisPage.jsx')))
const IntelligenceWatchingPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/WatchingPage.jsx')))
const IntelligenceWatchingAdsPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/WatchingAdsPage.jsx')))
const IntelligenceWatchListLayout = Loadable(lazy(() => import('../pages/dashboard/intelligence/WatchListLayout.jsx')))
const IntelligenceWatchListStatsPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/WatchListStatsPage.jsx')))
const IntelligenceBoardsPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/BoardsPage.jsx')))
const IntelligenceBoardDetailPage = Loadable(lazy(() => import('../pages/dashboard/intelligence/BoardDetailPage.jsx')))
const AQAPage = Loadable(lazy(() => import('../pages/dashboard/library/AQAPage.jsx')))
const ChatPage = Loadable(lazy(() => import('../pages/dashboard/library/ChatPage.jsx')))
const ChatHistoryPage = Loadable(lazy(() => import('../pages/dashboard/chat/ChatHistoryPage.jsx')))
const InsightsPage = Loadable(lazy(() => import('../pages/dashboard/insights/InsightsPage.jsx')))
const VideoExplorerPage = Loadable(lazy(() => import('../pages/dashboard/creative/video-explorer/VideoExplorerPage.jsx')))
const ClipsLibraryPage = Loadable(lazy(() => import('../pages/dashboard/clips-library/ClipsLibraryPage.jsx')))
const BoardView = Loadable(lazy(() => import('../pages/dashboard/clips-library/BoardView.jsx')))
const CompetitorsPage = Loadable(lazy(() => import('../pages/dashboard/research/competitors/CompetitorsPage.jsx')))
const DiscoveryPage = Loadable(lazy(() => import('../pages/dashboard/research/Discovery.jsx')))
const BoardPage = Loadable(lazy(() => import('../pages/dashboard/research/board/BoardPage.jsx')))
const OverviewPage = Loadable(lazy(() => import('../pages/dashboard/overview/OverviewPage.jsx')))
const HomePage = Loadable(lazy(() => import('../pages/dashboard/home/HomePage.jsx')))
const PlaygroundPage = Loadable(lazy(() => import('../pages/dashboard/playground/Playground.jsx')))
const ViewLayout = Loadable(lazy(() => import('../pages/dashboard/views/ViewLayout.jsx')))
const ViewsIndex = Loadable(lazy(() => import('../pages/dashboard/views/ViewsIndex.jsx')))
const ViewPage = Loadable(lazy(() => import('../pages/dashboard/views/ViewPage.jsx')))

// Dashboard -> Settings
const AccountGeneralPage = Loadable(lazy(() => import('../pages/dashboard/settings/account/AccountGeneralPage.jsx')))
const AccountDataConnectionsPage = Loadable(lazy(() => import('../pages/dashboard/settings/account/AccountDataConnectionsPage.jsx')))
const AccountSpacesPage = Loadable(lazy(() => import('../pages/dashboard/settings/account/account-spaces/AccountSpacesPage.jsx')))
const AccountMembersPage = Loadable(lazy(() => import('../pages/dashboard/settings/account/account-members/AccountMembersPage.jsx')))
const AccountBillingPage = Loadable(lazy(() => import('../pages/dashboard/settings/account/AccountBillingPage.jsx')))
const SpaceGeneralPage = Loadable(lazy(() => import('../pages/dashboard/settings/space/SpaceGeneralPage.jsx')))
const SpaceDataSourcesPage = Loadable(lazy(() => import('../pages/dashboard/settings/space/SpaceDataSourcesPage.jsx')))
const SpaceMembersPage = Loadable(lazy(() => import('../pages/dashboard/settings/space/space-members/SpaceMembersPage.jsx')))
const UserGeneralPage = Loadable(lazy(() => import('../pages/dashboard/settings/user/UserGeneralPage.jsx')))
const SpaceDataConfigPage = Loadable(lazy(() => import('../pages/dashboard/settings/space/SpaceDataConfigPage.jsx')))
const SpaceNamingConvPage = Loadable(lazy(() => import('../pages/dashboard/settings/space/SpaceNamingConvPage.jsx')))
const AiContextPage = Loadable(lazy(() => import('../pages/dashboard/settings/space/AiContextPage.jsx')))
const AiKnowledgePage = Loadable(lazy(() => import('../pages/dashboard/settings/space/AiKnowledgePage.jsx')))
const AiInstructionsPage = Loadable(lazy(() => import('../pages/dashboard/settings/space/AiInstructionsPage.jsx')))
const AiPreferencesPage = Loadable(lazy(() => import('../pages/dashboard/settings/space/AiPreferencesPage.jsx')))
const SpaceCustomMetricsPage = Loadable(lazy(() => import('../pages/dashboard/settings/space/SpaceCustomMetricsPage.jsx')))

// Connect
const KlaviyoConnect = Loadable(lazy(() => import('../pages/connect/Klaviyo.jsx')))
const GoogleAdsConnect = Loadable(lazy(() => import('../pages/connect/GoogleAds.jsx')))
const ShopifyConnect = Loadable(lazy(() => import('../pages/connect/shopify/Shopify.jsx')))
const TikTokConnect = Loadable(lazy(() => import('../pages/connect/TikTok.jsx')))
const LinkedInConnect = Loadable(lazy(() => import('../pages/connect/LinkedIn.jsx')))
const LinkedInStart = Loadable(lazy(() => import('../pages/connect/LinkedInStart.jsx')))
// Publicly Accessible Pages
const SnapshotPage = Loadable(lazy(() => import('../pages/public/snapshot/SnapshotPage.jsx')))
const SharedReportPage = Loadable(lazy(() => import('../pages/public/shared-report/SharedReportPage.jsx')))
const ExternalAdPage = Loadable(lazy(() => import('../pages/public/external-ad/ExternalAdPage.jsx')))
const BrandStrategySnapshopPage = Loadable(lazy(() => import('../pages/public/brand-strategy-snapshot/BrandStrategySnapshopPage.jsx')))
const PublicBoardPage = Loadable(lazy(() => import('../pages/public/board/PublicBoardPage.jsx')))
const PublicIntelligenceAdPage = Loadable(lazy(() => import('../pages/public/intelligence/PublicAdPage.jsx')))
const PublicIntelligenceBoardPage = Loadable(lazy(() => import('../pages/public/intelligence/PublicBoardPage.jsx')))
const CreativeSharePage = Loadable(lazy(() => import('../pages/public/creative-share/CreativeSharePage.jsx')))
const LetsGoPage = Loadable(lazy(() => import('../pages/public/lets-go/LetsGoPage.jsx')))

// Public Payment & Verify Payments Pages
const PaddlePublicCheckoutPage = Loadable(lazy(() => import('../pages/payment/paddle/PaddlePublicCheckoutPage.jsx')))
const VerifyShopifyPaymentPage = Loadable(lazy(() => import('../pages/payment/shopify/VerifyShopifyPaymentPage.jsx')))


// TODO - continue the flow
//  1. CRUD for the spaces
//  2. Add / remove user to a space
//  3. Change user's role in a space

export const router = createBrowserRouter([
    {
        errorElement: <RouteErrorBoundary />,
        path: "/",
        element: <Navigate to={AUTH_BASE_PATH + (window.location.search || '')} />,
        index: true
    },
    {
        errorElement: <RouteErrorBoundary />,
        path: 'auth',
        element: (
            <AuthLayout />
        ),
        children: [
            {
                path: "login",
                element: <SignInPage />,
            },
            {
                path: 'accept-invitation',
                element: <AcceptInvitationPage />
            },
            {
                path: "register",
                element: <RegisterPage />,
            },
            {
                path: "verify-auth",
                element: <VerifyAuthPage />,
            }
        ]
    },
    {
        errorElement: <RouteErrorBoundary />,
        path: 'payment',
        children: [
            {
                element: <Navigate to={AUTH_BASE_PATH + (window.location.search || '')} />,
                index: true,
            },
            {
                path: 'paddle',
                element: <PaddlePublicCheckoutPage />
            },
            {
                path: 'shopify',
                element: <VerifyShopifyPaymentPage />
            }
        ]
    },
    {
        errorElement: <RouteErrorBoundary />,
        path: 'lets-go',
        element: <LetsGoPage />,
    },
    {
        errorElement: <RouteErrorBoundary />,
        path: 'mcp',
        children: [
            {
                path: 'connect',
                element: <McpConnectPage />,
            }
        ]
    },
    {
        errorElement: <RouteErrorBoundary />,
        path: 'public',
        children: [
            {
                element: <Navigate to="/" />,
                index: true
            },
            {
                path: 'snapshot/:snapshotId',
                element: <SnapshotDataProvider><SnapshotPage /></SnapshotDataProvider>
            },
            {
                path: 'preview-ad/:externalAdId',
                element: <ExternalAdDataProvider><ExternalAdPage /></ExternalAdDataProvider>
            },
            {
                path: 'brand-strategy/:snapshotId',
                element: <BrandStrategySnapshopDataProvider><BrandStrategySnapshopPage /></BrandStrategySnapshopDataProvider>
            },
            {
                path: 'board/:boardId',
                element: <PublicBoardDataProvider><PublicBoardPage /></PublicBoardDataProvider>
            },
            {
                path: 'intelligence/ad/:adId',
                element: <PublicIntelligenceAdPage />
            },
            {
                path: 'intelligence/boards/:boardId',
                element: <PublicIntelligenceBoardPage />
            },
            {
                path: 'creative/:creativeId',
                element: <CreativeShareDataProvider><CreativeSharePage /></CreativeShareDataProvider>
            },
            {
                path: 'shared/report/:shareableReportId',
                element: <SharedReportDataProvider><SharedReportPage /></SharedReportDataProvider>
            },
            {
                path: "*",
                element: <Navigate to="/" />
            },
        ]
    },
    {
        errorElement: <RouteErrorBoundary />,
        path: 'connect',
        element: (
            // <AuthGuard>
            // <div className="h-full bg-main-surface bg-center bg-cover bg-no-repeat overflow-hidden relative">
            <Outlet />
            // </div>

            // </AuthGuard>
        ),
        children: [
            {
                path: "shopify",
                element: <ShopifyConnect />
            },
            {
                path: "klaviyo",
                element: <KlaviyoConnect />
            },
            {
                path: "google-ads",
                element: <GoogleAdsConnect />
            },
            {
                path: "tiktok",
                element: <TikTokConnect />
            },
            {
                path: "linkedin",
                element: <LinkedInConnect />
            }
        ]
    },
    {
        errorElement: <RouteErrorBoundary />,
        path: 'account',
        element: (
            <AuthGuard>
                <PaymentProvider>
                    <AccountDefault />
                </PaymentProvider>
            </AuthGuard>
        ),
        children: [
            {
                path: ':accountId',
                element: <AccountGuard />,
                children: [
                    {
                        path: 'connect/linkedin/start',
                        element: <LinkedInStart />
                    },
                    {
                        path: 'space',
                        element: <SpaceDefault />,
                        children: [
                            {
                                path: ':spaceId',
                                element: <SpaceGuard />,
                                children: [
                                    {
                                        element: <Navigate to="dashboard" replace />,
                                        index: true
                                    },
                                    {
                                        // Onboarding is a full-screen takeover that fetches its own data.
                                        // It lives OUTSIDE the dashboard's PaymentDataProvider /
                                        // DashboardDataProvider gates (which show a full-page loader while
                                        // they fetch), wrapped in only the context providers its hooks need,
                                        // so it renders immediately after register with no middle loader.
                                        path: "onboarding",
                                        element: (
                                            <DashboardProvider>
                                                <CreativeAnalyticsProvider>
                                                    <ResearchProvider>
                                                        <LibraryProvider>
                                                            <CreativeAiChatProvider>
                                                                <CreativeIntelligenceProvider>
                                                                    <OnboardingPage />
                                                                </CreativeIntelligenceProvider>
                                                            </CreativeAiChatProvider>
                                                        </LibraryProvider>
                                                    </ResearchProvider>
                                                </CreativeAnalyticsProvider>
                                            </DashboardProvider>
                                        )
                                    },
                                    {
                                        path: "dashboard",
                                        element: (
                                            <PaymentDataProvider>
                                                <FreeTierLockGuard>
                                                    <DashboardProvider>
                                                        <DashboardDataProvider>
                                                            <CreativeAnalyticsProvider>
                                                                <ResearchProvider>
                                                                    <LibraryProvider>
                                                                        <CreativeAiChatProvider>
                                                                            <CreativeIntelligenceProvider>
                                                                                <DashboardLayout />
                                                                            </CreativeIntelligenceProvider>
                                                                        </CreativeAiChatProvider>
                                                                    </LibraryProvider>
                                                                </ResearchProvider>
                                                            </CreativeAnalyticsProvider>
                                                        </DashboardDataProvider>
                                                    </DashboardProvider>
                                                </FreeTierLockGuard>
                                            </PaymentDataProvider>
                                        ),
                                        children: [
                                            {
                                                element: <Navigate to={DASHBOARD_ROOT} replace />,
                                                index: true
                                            },
                                            {
                                                path: "embeddings-search",
                                                element: <EmbeddingsSearchPage />
                                            },
                                            {
                                                path: "overview",
                                                element: <OverviewPage />
                                            },
                                            {
                                                path: "playground",
                                                element: <PlaygroundPage />
                                            },
                                            {
                                                path: "creative",
                                                children: [
                                                    {
                                                        index: true,
                                                        element: <CreativePage />
                                                    },
                                                    {
                                                        path: "home",
                                                        element: <HomePage />
                                                    },
                                                    {
                                                        path: "insights",
                                                        element: <InsightsPage />
                                                    },
                                                    // {
                                                    //     path: "video-explorer",
                                                    //     element: <VideoExplorerPage />
                                                    // }
                                                    {
                                                        path: "views",
                                                        element: <ViewLayout />,
                                                        children: [
                                                            { index: true, element: <ViewsIndex /> },
                                                            { path: ":viewId", element: <ViewPage /> },
                                                        ]
                                                    }
                                                ]

                                            },
                                            {
                                                // Legacy /research/board/:boardId (+ any sub-path) — same
                                                // Mongo board _id is reused by the new intelligence boards
                                                // route, so this deep-link translates cleanly. The trailing
                                                // /* catches anything under the board path (e.g. an old
                                                // /research/board/:id/edit URL someone bookmarked) and
                                                // still lands them on the right board.
                                                path: "research/board/:boardId/*",
                                                element: <ResearchBoardRedirect />,
                                            },
                                            {
                                                // Legacy /research/all-brands was the "brands you follow"
                                                // table. The new equivalent is /intelligence/watching
                                                // (same shape, watch-list scoped). Keep the deep link
                                                // working for any bookmarks.
                                                path: "research/all-brands",
                                                element: <ResearchRedirectTo dest="watching" />,
                                            },
                                            {
                                                // Everything else under /research/* (incl. /research,
                                                // /research/competitors, /research/:mongoAdvertiserId)
                                                // lands at /library — soft landing. We can't
                                                // auto-translate /research/:mongoAdvertiserId to the new
                                                // /intelligence/brands/:pageId without an API hop because
                                                // the ID spaces differ (Mongo _id vs FB pageId).
                                                // ResearchDataProvider, AllBrandsPage, BrandPage,
                                                // CompetitorsPage, DiscoveryPage, BoardPage are still
                                                // imported above but no longer rendered; safe to delete
                                                // in a follow-up cleanup once nothing else references them.
                                                path: "research/*",
                                                element: <ResearchRedirectTo dest="library" />,
                                            },
                                            {
                                                // API area — developer console (Phase 1: UI on mock data).
                                                path: "api",
                                                element: <Outlet />,
                                                children: [
                                                    { index: true, element: <ApiOverviewPage /> },
                                                    { path: "usage", element: <ApiUsagePage /> },
                                                    { path: "billing", element: <ApiBillingPage /> },
                                                    { path: "support", element: <ApiSupportPage /> },
                                                    { path: "*", element: <Navigate to=".." replace /> },
                                                ]
                                            },
                                            {
                                                // /intelligence — new v2 module. Parallel to /research,
                                                // completely separate UI based on upspring-mock layout,
                                                // sourced from the-core via spring-backend proxy.
                                                // No nav link yet — direct-URL access during dev.
                                                path: "intelligence",
                                                element: <IntelligenceProvider><Outlet /></IntelligenceProvider>,
                                                children: [
                                                    // Subnav (Library / Discovery) lives in the
                                                    // sidebar via IntelligenceSubNavbar — selected
                                                    // by NavBar.jsx when pathname includes
                                                    // /intelligence. No in-page layout wrapper.
                                                    {
                                                        index: true,
                                                        element: <Navigate to="discovery" replace />
                                                    },
                                                    {
                                                        path: "library",
                                                        element: <IntelligenceLibraryPage />
                                                    },
                                                    {
                                                        path: "landing-pages",
                                                        element: <IntelligenceLandingPagesPage />
                                                    },
                                                    {
                                                        // PLG home mockup (UI only) — Lovable-style prompt
                                                        // hero over ad strips. Throwaway route; real library
                                                        // above is untouched.
                                                        path: "library/home",
                                                        element: <IntelligenceLibraryHomePage />
                                                    },
                                                    {
                                                        // Answer Workspace mockup (UI only) — split chat rail
                                                        // + evolving ads board the home prompt flows into.
                                                        path: "library/answer",
                                                        element: <IntelligenceLibraryAnswerPage />
                                                    },
                                                    {
                                                        // "One continuous surface" mockup (UI only) — narrated
                                                        // answer bands inside the library + floating chat.
                                                        path: "library/surface",
                                                        element: <IntelligenceLibrarySurfacePage />
                                                    },
                                                    {
                                                        // "Command bar" mockup (UI only) — full-width ad bands +
                                                        // a center-bottom bar that expands into the chat.
                                                        path: "library/command",
                                                        element: <IntelligenceLibraryCommandPage />
                                                    },
                                                    {
                                                        // Advertisers browse surface (UI-only mock) —
                                                        // list advertisers by vertical/category.
                                                        path: "advertisers",
                                                        element: <IntelligenceAdvertisersPage />
                                                    },
                                                    {
                                                        path: "discovery",
                                                        element: <IntelligenceDiscoveryPage />
                                                    },
                                                    {
                                                        path: "watching",
                                                        element: <IntelligenceWatchingPage />
                                                    },
                                                    {
                                                        path: "watching/ads",
                                                        element: <IntelligenceWatchingAdsPage />
                                                    },
                                                    {
                                                        // "All advertisers" hub: every watched advertiser's
                                                        // ads, plus a Stats table of what changed.
                                                        path: "watching/all",
                                                        element: <IntelligenceWatchListLayout />,
                                                        children: [
                                                            {
                                                                path: "stats",
                                                                element: <IntelligenceWatchListStatsPage />
                                                            },
                                                            {
                                                                index: true,
                                                                element: <IntelligenceWatchingAdsPage embedded />
                                                            },
                                                        ]
                                                    },
                                                    {
                                                        path: "boards",
                                                        element: <IntelligenceBoardsPage />
                                                    },
                                                    {
                                                        path: "boards/:boardId",
                                                        element: <IntelligenceBoardDetailPage />
                                                    },
                                                    {
                                                        path: "brands/:advertiserId",
                                                        element: <IntelligenceBrandLayout />,
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: <IntelligenceBrandPage />
                                                            },
                                                            {
                                                                path: "ads",
                                                                element: <IntelligenceAllAdsPage />
                                                            },
                                                            {
                                                                path: "landing-pages",
                                                                element: <IntelligenceBrandLandingPagesPage />
                                                            },
                                                            {
                                                                path: "hooks",
                                                                element: <IntelligenceHookAnalysisPage />
                                                            },
                                                            {
                                                                path: "analysis",
                                                                element: <IntelligenceBrandAnalysisPage />
                                                            },
                                                            {
                                                                path: "creators",
                                                                element: <IntelligenceCreatorsPage />
                                                            },
                                                            {
                                                                path: "reddit",
                                                                element: <IntelligenceRedditPage />
                                                            },
                                                            {
                                                                path: "similar",
                                                                element: <IntelligenceSimilarAdvertisersPage />
                                                            },
                                                        ]
                                                    },
                                                    {
                                                        path: "landing-pages/:pageId",
                                                        element: <IntelligenceLandingPageDetailPage />
                                                    },
                                                    {
                                                        path: "brands/:advertiserId/ads/:adId",
                                                        element: <IntelligenceAdDetailPage />
                                                    },
                                                ]
                                            },
                                            {
                                                path: "ads-qa",
                                                element: <AQAPage />,
                                            },
                                            {
                                                path: "chat",
                                                element: <ChatDataProvider />,
                                                children: [
                                                    {
                                                        index: true,
                                                        element: <ChatHistoryPage />
                                                    },
                                                    {
                                                        path: "settings/context",
                                                        element: <AiContextPage />
                                                    },
                                                    {
                                                        path: "settings/knowledge",
                                                        element: <AiKnowledgePage />
                                                    },
                                                    {
                                                        path: "settings/instructions",
                                                        element: <AiInstructionsPage />
                                                    },
                                                    {
                                                        path: "settings/preferences",
                                                        element: <AiPreferencesPage />
                                                    },
                                                    {
                                                        path: ":chatId",
                                                        element: <ChatHistoryPage />
                                                    }
                                                ]
                                            },
                                            {
                                                path: "library",
                                                children: [
                                                    {
                                                        index: true,
                                                        element: <LibraryPage />
                                                    },
                                                    {
                                                        path: "aqa",
                                                        element: <AQAPage />
                                                    },
                                                    {
                                                        path: ":folderId",
                                                        element: <FolderPage />
                                                    }
                                                ]
                                            },
                                            {
                                                path: "clips-library",
                                                children: [
                                                    {
                                                        index: true,
                                                        element: <ClipsLibraryPage />
                                                    },
                                                    {
                                                        path: 'board/:boardId',
                                                        element: <BoardView />
                                                    }
                                                ]
                                            },
                                            {
                                                path: "integrations",
                                                children: [
                                                    {
                                                        index: true,
                                                        // element: <IntegrationsPage />,
                                                    },
                                                    {
                                                        path: "connect-klaviyo",
                                                        element: <KlaviyoConnect />
                                                    }
                                                ]
                                            },
                                            {
                                                path: "settings",
                                                children: [
                                                    {
                                                        path: '',
                                                        element: <Navigate to="account/general" replace />
                                                    },
                                                    {
                                                        path: 'account',
                                                        children: [
                                                            {
                                                                path: '',
                                                                element: <Navigate to="general" replace />
                                                            },
                                                            {
                                                                path: 'general',
                                                                element: <AccountGeneralPage />
                                                            },
                                                            {
                                                                path: 'spaces',
                                                                element: <AccountSpacesDataProvider><AccountSpacesPage /></AccountSpacesDataProvider>
                                                            },
                                                            {
                                                                path: 'data-connections',
                                                                element: <AccountDataConnectionsPage />
                                                            },
                                                            {
                                                                path: 'members',
                                                                element: <AccountMembersDataProvider><AccountMembersPage /></AccountMembersDataProvider>
                                                            },
                                                            {
                                                                path: 'billing',
                                                                element: <AccountBillingPage />
                                                            }
                                                        ]
                                                    },
                                                    {
                                                        path: 'space',
                                                        children: [
                                                            {
                                                                path: '',
                                                                element: <Navigate to="details" replace />
                                                            },
                                                            {
                                                                path: 'general',
                                                                element: <SpaceGeneralPage />
                                                            },
                                                            {
                                                                path: 'data-sources',
                                                                element: <SpaceDataSourcesPage />
                                                            },
                                                            {
                                                                path: 'data-config',
                                                                element: <SpaceDataConfigPage />
                                                            },
                                                            {
                                                                path: 'members',
                                                                element: <SpaceMembersDataProvider><SpaceMembersPage /></SpaceMembersDataProvider>
                                                            },
                                                            {
                                                                path: 'naming-conventions',
                                                                element: <SpaceNamingConvPage />
                                                            },
                                                            {
                                                                path: 'custom-metrics',
                                                                element: <SpaceCustomMetricsPage />
                                                            },
                                                            {
                                                                path: 'ai-context',
                                                                element: <AiContextPage />
                                                            },
                                                            {
                                                                path: 'ai-knowledge',
                                                                element: <AiKnowledgePage />
                                                            },
                                                            {
                                                                path: 'ai-instructions',
                                                                element: <AiInstructionsPage />
                                                            },
                                                            {
                                                                path: 'ai-preferences',
                                                                element: <AiPreferencesPage />
                                                            }
                                                        ]
                                                    },
                                                    {
                                                        path: 'user',
                                                        children: [
                                                            {
                                                                path: '',
                                                                element: <Navigate to="details" replace />
                                                            },
                                                            {
                                                                path: 'details',
                                                                element: <UserGeneralPage />
                                                            }
                                                        ]
                                                    }
                                                ]
                                            },
                                            {
                                                path: "*",
                                                element: <Navigate to={DASHBOARD_ROOT} />
                                            },
                                        ]
                                    },
                                ]
                            }
                        ]
                    }
                ]
            },
        ]
    },
    {
        errorElement: <RouteErrorBoundary />,
        path: "*",
        element: <Navigate to={AUTH_BASE_PATH + (window.location.search || '')} />
    },
])
